import { useRouter, type Href } from 'expo-router';
import { useEffect, useRef } from 'react';

import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import {
  buildLoginPathWithRedirect,
  buildSignupPathWithRedirect,
  useCurrentPagePath,
} from '@/hooks/useRedirectPath';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { trackCreateAccountCtaTriggered, trackSurveyAuthGateViewed } from '@/util/analyticsEvents';

/**
 * Shown in place of the guest survey form when a survey only accepts
 * responses tied to an account. Deciding this before any question renders is
 * deliberate: a visitor who fills in half a survey and is only then told to
 * sign in loses that work, since nothing carries answers across the auth hop.
 *
 * Both calls to action carry the current survey path as `redirect_path`, the
 * same mechanism the auth card uses, but that parameter does not survive
 * every hop of the signup chain. A user who still needs to confirm their
 * account details gets routed to that confirmation step with no params
 * forwarded, so `redirect_path` is gone before that step ever looks for it.
 * A brand-new signup can also be routed straight into the new-question
 * flow under its own feature flag, bypassing `redirect_path` entirely. Both
 * are known gaps in the redirect chain rather than bugs to fix here — treat
 * them as a landmine, and re-check whether a change to either flow still
 * drops the survey's return path.
 */
export function SurveyAuthRequired({ surveyGroupId }: { surveyGroupId: string }) {
  const router = useRouter();

  const currentPath = useCurrentPagePath();

  const hasTrackedViewRef = useRef(false);

  useEffect(() => {
    if (hasTrackedViewRef.current) {
      return;
    }

    hasTrackedViewRef.current = true;

    trackSurveyAuthGateViewed({ surveyGroupId });
  }, [surveyGroupId]);

  return (
    <VStack className="mx-auto w-full max-w-[500px] flex-1 items-center justify-center px-6">
      <Heading size="md" className="text-center">
        Sign in to continue
      </Heading>

      <Text className="pt-3 text-center text-typography-700">
        This survey is linked to your Primum account, so we know whose responses these are.
      </Text>

      <HStack className="gap-3 pt-5">
        <Button
          action="primary"
          size="md"
          onPress={() => {
            router.push(
              (currentPath ? buildLoginPathWithRedirect(currentPath) : ROUTES.LOGIN) as Href
            );
          }}
          {...getAccessibilityProps({
            label: 'Log in to answer this survey',
            role: 'button',
          })}>
          <ButtonText>Log in</ButtonText>
        </Button>

        <Button
          action="secondary"
          variant="outline"
          size="md"
          className="border-outline-500"
          onPress={() => {
            trackCreateAccountCtaTriggered({ cta: 'sign_up', source: 'survey_auth_gate' });

            router.navigate(
              (currentPath
                ? buildSignupPathWithRedirect(currentPath)
                : ROUTES.CREATE_ACCOUNT) as Href
            );
          }}
          {...getAccessibilityProps({
            label: 'Create account to answer this survey',
            role: 'button',
          })}>
          <ButtonText className="text-typography-700">Create account</ButtonText>
        </Button>
      </HStack>

      <Text className="pt-4 text-center text-sm text-typography-700">
        You&apos;ll come back to this survey when you&apos;re done.
      </Text>
    </VStack>
  );
}
